/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { Link } from 'react-router';
import { Text } from '@/shared/ui';
import type { DashboardResponse, IncompleteListingItem } from '@/services/housien/dashboard';

type TFn = (key: string, options?: Record<string, unknown>) => string;

/**
 * Rows are capped: a panel that scrolls is a list, and lists have pages.
 *
 * Six beside fourteen tiles, where the panel has a tall column to fill.
 * The stacked screens ask for fewer: there the panel is the last thing on
 * the page, and a row that pushes the legend under the fold costs more
 * than the row is worth.
 */
const MAX_ROWS = 6;

const ACCENT = {
  cobre: 'var(--accent-cobre)',
  mostarda: 'var(--accent-mostarda)',
  terracota: 'var(--accent-terracota)',
};

interface Row {
  id: string;
  title: string;
  sub: string;
  /** Right-hand column: the age, in the app's own face and tabular. */
  meta: string;
  dot: string;
  urgent: boolean;
  to: string;
}

/** Rows from the overdue rent, longest overdue first (the payload's order). */
function overdueRows(t: TFn, data: DashboardResponse | null, agencyId: string): Row[] {
  return (data?.overdue_rent?.items ?? []).map((item) => ({
    id: `overdue-${item.lease_id}-${item.accrual_period}`,
    title: t('dashboard.panel.overdue.title', { period: item.accrual_period }),
    sub: t('dashboard.panel.overdue.sub', {
      amount: ((item.amount_cents ?? 0) / 100).toLocaleString('pt-BR', {
        style: 'currency', currency: 'BRL', maximumFractionDigits: 0,
      }),
    }),
    meta: t('dashboard.panel.days', { count: item.days_late ?? 0 }),
    dot: ACCENT.cobre,
    urgent: true,
    to: `/app/agencies/${agencyId}/leases/${item.lease_id}`,
  }));
}

/** Rows from the contracts running out, soonest first. */
function expiringRows(t: TFn, data: DashboardResponse | null, agencyId: string): Row[] {
  return (data?.expiring_contracts?.items ?? []).map((item) => ({
    id: `expiring-${item.lease_id}`,
    title: t('dashboard.panel.expiring.title'),
    sub: t('dashboard.panel.expiring.sub', { count: item.days_until_end ?? 0 }),
    meta: t('dashboard.panel.days', { count: item.days_until_end ?? 0 }),
    dot: ACCENT.mostarda,
    // A term already past is the only expiry that cannot wait.
    urgent: (item.days_until_end ?? 0) < 0,
    to: `/app/agencies/${agencyId}/leases/${item.lease_id}`,
  }));
}

/**
 * Names a listing whose headline was never written.
 *
 * A row that lists what is missing has to say *which* listing, and the one
 * flagged for having no photo, no price and no address is exactly the one
 * nobody wrote a headline for. The backend sends the facts; the words
 * between them are this side's business.
 *
 * @param t - The namespace's translator.
 * @param item - The flagged listing.
 * @returns A label a reader can tell two rows apart by.
 */
function listingLabel(t: TFn, item: IncompleteListingItem): string {
  const bedrooms = item.bedrooms == null
    ? ''
    : t('dashboard.panel.incomplete.bedrooms', { count: item.bedrooms });
  const area = item.area == null
    ? ''
    : t('dashboard.panel.incomplete.area', { value: item.area });
  const parts = [item.category || '', bedrooms, area].filter(Boolean);
  return parts.length > 0
    ? parts.join(' · ')
    : t('dashboard.panel.incomplete.untitled');
}

/** Rows from the listings that cannot be published as they stand. */
function incompleteRows(t: TFn, data: DashboardResponse | null, agencyId: string): Row[] {
  return (data?.incomplete_listings?.items ?? []).map((item) => ({
    id: `incomplete-${item.id}`,
    title: item.title || listingLabel(t, item),
    sub: (item.missing ?? [])
      .map((what) => t(`dashboard.panel.incomplete.${what}`))
      .join(' · '),
    meta: '—',
    dot: ACCENT.mostarda,
    urgent: false,
    to: `/app/agencies/${agencyId}/properties/${item.id}`,
  }));
}

/** Rows from the clients waiting on an answer, longest wait first. */
function withoutReturnRows(t: TFn, data: DashboardResponse | null, agencyId: string): Row[] {
  return (data?.clients_without_return ?? []).map((client) => ({
    id: `client-${client.client_id}`,
    title: client.client_name ?? '',
    sub: t('dashboard.panel.withoutReturn.sub'),
    meta: client.days_since_contact === null || client.days_since_contact === undefined
      ? '—'
      : t('dashboard.panel.days', { count: client.days_since_contact }),
    dot: ACCENT.terracota,
    urgent: (client.days_since_contact ?? 0) > 30,
    to: `/app/agencies/${agencyId}/clients/${client.client_id}`,
  }));
}

/**
 * The rows, one kind at a time.
 *
 * Deliberately a round robin rather than a single sort: the four kinds
 * measure different clocks — days overdue, days until a term ends, days
 * since a visit — and ranking them against each other would mean deciding
 * that an eighty-day silence outranks a contract ending on Friday, which
 * is the reader's call and not ours. Taking the worst of each kind in turn
 * guarantees no kind is pushed off the panel by another kind's tail, which
 * is what the old three separate lists were really protecting.
 */
function interleave(sources: Row[][], limit: number): Row[] {
  const rows: Row[] = [];
  const depth = Math.max(0, ...sources.map((source) => source.length));
  for (let index = 0; index < depth && rows.length < limit; index += 1) {
    sources.forEach((source) => {
      if (source[index] && rows.length < limit) rows.push(source[index]);
    });
  }
  return rows;
}

/**
 * "Precisa de você hoje": the one list that replaced three.
 *
 * It stands in for the old "meu dia" block, the urgency strip and the
 * overdue-rent table at once — the same rows, in one place, each carrying
 * the age that makes it urgent and a link to the record itself.
 *
 * Listings nobody has visited are counted by a tile and named nowhere
 * here. In a book of twenty thousand where fifty have ever been shown, the
 * other nineteen thousand are all equally "stale", and a panel listing six
 * of them at random is asking the reader to find a visit for the whole
 * portfolio one row at a time. The number is the honest form of that fact;
 * a to-do list is not.
 *
 * @param props.t - Translation function
 * @param props.data - The dashboard payload
 * @param props.agencyId - Active agency, for the destinations
 * @param props.maxRows - How many rows this screen has room for
 */
export function AttentionPanel({
  t, data, agencyId, maxRows = MAX_ROWS,
}: {
  t: TFn;
  data: DashboardResponse | null;
  agencyId: string;
  maxRows?: number;
}) {
  const rows = interleave([
    overdueRows(t, data, agencyId),
    expiringRows(t, data, agencyId),
    withoutReturnRows(t, data, agencyId),
    incompleteRows(t, data, agencyId),
  ], maxRows);

  return (
    <section
      data-testid="overview-attention-panel"
      className="min-w-0 overflow-hidden rounded-(--radius-lg) border border-(--border-subtle) bg-[var(--surface-raised)]"
    >
      <div className="flex items-baseline gap-(--space-2) border-b border-(--border-ultra-subtle) px-(--space-4) py-(--space-3)">
        <Text
          as="span"
          className="text-(length:--text-app-section) font-[var(--font-bold)] text-[var(--text-primary)]"
        >
          {t('dashboard.panel.title')}
        </Text>
        <Text as="span" className="text-(length:--text-meta) text-[var(--text-tertiary-soft)]">
          {t('dashboard.panel.hint')}
        </Text>
      </div>

      {rows.length === 0 ? (
        <Text
          as="span"
          className="block px-(--space-4) py-(--space-4) text-(length:--text-body-sm) text-[var(--text-tertiary)]"
        >
          {t('dashboard.panel.empty')}
        </Text>
      ) : rows.map((row) => (
        <Link
          key={row.id}
          to={row.to}
          data-testid={`overview-attention-row-${row.id}`}
          className="flex w-full items-center gap-(--space-3) border-b border-(--border-ultra-subtle) px-(--space-4) py-(--space-2-5) no-underline transition-colors hover:bg-[var(--surface-hover)]"
        >
          <Text
            as="span"
            className="h-[6px] w-[6px] flex-none rounded-(--radius-full)"
            style={{ background: row.dot }}
          />
          <Text as="span" className="flex min-w-0 flex-1 flex-col gap-(--space-0-5)">
            <Text
              as="span"
              className="truncate text-(length:--text-body-sm) font-[var(--font-semibold)] text-[var(--text-primary)]"
            >
              {row.title}
            </Text>
            <Text as="span" className="truncate text-(length:--text-meta) text-[var(--text-tertiary-soft)]">
              {row.sub}
            </Text>
          </Text>
          <Text
            as="span"
            /*
              * `tabular-nums` and not a mono face. The column was set in
              * --font-family-mono to keep the ages in a straight edge, but
              * that is what tabular figures are for -- the app's own face
              * already has them, and they line up just as well. The mono
              * only bought the alignment at the price of a second typeface
              * in a card whose every other line is the app's.
              */
            className="flex-none text-(length:--text-meta) tabular-nums"
            style={{ color: row.urgent ? ACCENT.cobre : 'var(--text-tertiary-soft)' }}
          >
            {row.meta}
          </Text>
        </Link>
      ))}
    </section>
  );
}
